import { EBitfApiSortDirection, EBitfParsers } from '@enums';
import { default as deepmerge, default as merge } from 'deepmerge';
import qs from 'qs';
import { IBitfApiRequest, IBitfApiSorting } from '../../services/api/bitf-api.interface';
import { IBitfRequestMapper } from '../bitf-parsers.interface';
import { BitfRestRequestMapper } from '../rest-parser';
import { EBitfStrapiQueryComparator, EBitfStrapiQueryOperator } from './';
import { IBitfStrapiFormItemsConfigFilter } from './bitf-strapi-api.interface';
export class BitfStrapiRequestMapper extends BitfRestRequestMapper implements IBitfRequestMapper {
  name = EBitfParsers.BITF_STRAPI_PARSER;

  constructor() {
    super();
  }

  map(requestParams: IBitfApiRequest) {
    const {
      search,
      filter,
      sorting,
      page,
      size,
      populate,
      fields,
      query,
      // queryParams,
      apiParser,
    } = requestParams;
    requestParams.apiParser = apiParser || EBitfParsers.BITF_STRAPI_PARSER;
    const requestMapped = super.map(requestParams);

    const strapiQueryParams: any = { filters: { $or: [] }, pagination: {} };

    if (search) {
      Object.entries(search).forEach(([key, value]) => {
        strapiQueryParams.filters.$or.push({ [key]: { [EBitfStrapiQueryComparator.CONTAINS]: value } });
      });
    }

    // Query is used only for FormItemsConfig
    if (query?.length) {
      (query as IBitfStrapiFormItemsConfigFilter[])
        .filter((queryItem) => queryItem.property)
        .forEach((queryItem) => {
          if (queryItem.comparator === EBitfStrapiQueryComparator.NOT_NULL_OR_EMPTY) {
            const isValued = !!queryItem.value;
            if (isValued) {
              // not null AND not empty string
              const condition = this.buildCondition(
                queryItem.property,
                {
                  [EBitfStrapiQueryComparator.NOT_NULL]: true,
                  [EBitfStrapiQueryComparator.NOT_EQUAL]: '',
                },
                queryItem.metaData
              );
              Object.assign(strapiQueryParams.filters, deepmerge(strapiQueryParams.filters, condition));
            } else {
              // null OR empty string
              const nullCondition = this.buildCondition(queryItem.property, {
                [EBitfStrapiQueryComparator.NULL]: true,
              });
              const emptyCondition = this.buildCondition(queryItem.property, {
                [EBitfStrapiQueryComparator.EQUAL]: '',
              });
              strapiQueryParams.filters.$or.push(nullCondition);
              strapiQueryParams.filters.$or.push(emptyCondition);
            }
            return;
          }
          // Ref: { [queryItem.property]: { [queryItem.comparator]: queryItem.value } };
          const condition = this.buildCondition(
            queryItem.property,
            {
              [queryItem.comparator]: queryItem.value,
            },
            queryItem.metaData
          );
          if (queryItem.operator === EBitfStrapiQueryOperator.AND) {
            Object.assign(strapiQueryParams.filters, deepmerge(strapiQueryParams.filters, condition));
          } else {
            strapiQueryParams.filters.$or.push(condition);
          }
        });
    }

    if (page !== undefined) {
      strapiQueryParams.pagination.page = page;
    }

    if (size !== undefined) {
      strapiQueryParams.pagination.pageSize = size;
    }

    if (sorting?.length) {
      if (!sorting.some((sort) => sort.property === 'id')) {
        sorting.push({ property: 'id', direction: EBitfApiSortDirection.DESC });
      }
      strapiQueryParams.sort = sorting.map(
        (sort: IBitfApiSorting) => `${sort.property}:${sort.direction.toLowerCase()}`
      );
    }

    if (populate) {
      strapiQueryParams.populate = populate;
    }

    if (fields && fields.length) {
      strapiQueryParams.fields = fields;
    }

    if (filter) {
      strapiQueryParams.filters = merge(strapiQueryParams.filters, filter);
    }

    Object.assign(requestMapped.params, this.mapStrapiQueryParams(strapiQueryParams));

    return requestMapped;
  }

  private mapStrapiQueryParams(strapiQueryParams) {
    const stringifiedStrapiQueryParams = qs.stringify(strapiQueryParams, { encode: false });

    if (!stringifiedStrapiQueryParams) {
      return {};
    }

    const mappedStrapiQueryParams = stringifiedStrapiQueryParams.split('&').map((rule) => rule.split('='));

    return Object.fromEntries(mappedStrapiQueryParams);
  }

  private buildCondition(key: string, objValue: any, metaData?: any): any {
    const filterObject = {};
    key.split('.').reduce((o, s, index) => {
      if (index >= key.split('.').length - 1) {
        o[s] = objValue;
      } else {
        o[s] = {};
      }
      return o[s];
    }, filterObject);

    // Add custom filters from the BE
    /**
     * Example BE Form Item
     * metaData: {
        filter: {
          property: {
            value: "Demo",
          },
        },
      },
     */
    if (metaData?.filter) {
      Object.assign(filterObject, deepmerge(filterObject, metaData?.filter));
    }
    return filterObject;
  }
}
